// components/ProtectedRoute.jsx
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import Loader from "./common/commonUtils/Loader";
import EmailVerifyBanner from "./authentication/EmailVerifyBanner";

/**
 * Props:
 * - roles?: string[]         // e.g., ['SUPER_ADMIN']
 * - requireAll?: boolean     // default false (ANY of the roles)
 * - redirectTo?: string      // where to send unauthenticated users (default '/login')
 * - unauthorizedTo?: string  // where to send authorized-but-forbidden users (default '/home')
 */
export default function ProtectedRoute({
  roles,
  requireAll = false,
  redirectTo = "/login",
  unauthorizedTo = "/home",
}) {
  const { isAuthenticated, isBootstrapping, user } = useAuth();
  const location = useLocation();

  if (isBootstrapping) {
    return <Loader variant="page" text="Restoring your session…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  if (Array.isArray(roles) && roles.length > 0) {
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    const hasAccess = requireAll
      ? roles.every((r) => userRoles.includes(r))
      : roles.some((r) => userRoles.includes(r));

    if (!hasAccess) {
      return <Navigate to={unauthorizedTo} replace />;
    }
  }

  return (
    <>
      <EmailVerifyBanner emailVerified={user?.emailVerified} email={user?.email} />
      <Outlet />
    </>
  );
}
